import { useTranslation } from "react-i18next";

import { Mascot } from "../components/Mascot";
import { Shot } from "../components/Shot";
import { StoreMark } from "../components/StoreMark";
import { MASCOTS } from "../components/mascots";
import { SHOTS } from "../components/shots";
import { useHasKey } from "../components/useHasKey";
import { resolveConfig, type SiteConfig } from "../config";

/**
 * Three apps, three destinations, and nothing else.
 *
 * TWO OWNER DECISIONS ARE BAKED IN HERE (2026-08-27), and both undo something this section
 * used to do:
 *
 *  1. NO DESKTOP CARD. There will be no desktop application, so the page does not keep a
 *     placeholder for one.
 *  2. NO STATUS BADGES, AND NO "IN DEVELOPMENT" AT ALL. Every app on this list exists and is
 *     handed to a visitor as an app. The previous version derived a badge from whether a URL
 *     was configured, which meant a missing build argument silently relabelled a shipping
 *     product as unfinished — exactly the outcome the owner's rule forbids. Both the badge
 *     and that inference are gone; a card is a link, always.
 *
 * ⛔ STILL NO STORE BADGE ARTWORK. The official "Download on the App Store" and "Get it on
 * Google Play" lock-ups are separately licensed and are not allowed on this page. What the
 * iPhone and Android cards carry (owner's request, 2026-09-01) is the plain platform MARK next
 * to the page's own translated label — see components/StoreMark.tsx and
 * tools/build-store-icons.mjs. The mark is decorative; the text is still what names the link.
 *
 * Every card is one <a>: the whole cell is the target, so the action is unmissable at any
 * width without three different affordances competing inside one grid.
 */
const PLATFORMS = ["web", "ios", "android"] as const;

export function Platforms({ config }: { config: SiteConfig }) {
  const { t } = useTranslation();
  const has = useHasKey();

  // The production addresses live in config.ts; an unset build argument falls back to them
  // rather than to an empty href that would render a card leading nowhere. They are no longer
  // placeholders and the web card no longer points at stage — see config.ts for what that
  // cost, and why the pre-render now refuses to build a page carrying either.
  const urls = resolveConfig(config);
  const href: Record<(typeof PLATFORMS)[number], string> = {
    web: urls.webAppUrl,
    ios: urls.iosUrl,
    android: urls.androidUrl,
  };

  return (
    <section className="section" id="platforms">
      <div className="section__head">
        <span className="section__eyebrow">07</span>
        <h2 className="section__heading">{t("platforms.heading")}</h2>
      </div>

      {/* The Android build gets shown rather than described. Frames are swapped by editing
          SHOTS in components/shots.ts — the invite frame in particular is still pending an
          owner decision, and it is referenced from this one line only. */}
      <div className="showcase__frames showcase__frames--phones">
        {/* ⚠ ABSOLUTELY POSITIONED, AND THAT IS THE WHOLE POINT. This row of three phones is
            centred inside the content column by an explicit owner decision; a figure taking a
            grid track would have widened the row and dragged it off centre. Out of flow, it
            stands in the empty margin the centring already leaves on the left — see
            .showcase__frames--phones .mascot in sections.css, which also hides it at any width
            where that margin is too small to hold it. */}
        <Mascot mascot={MASCOTS.present} />
        <Shot shot={SHOTS.phoneChat} altKey="shots.phoneChat.alt" />
        <Shot shot={SHOTS.phoneCall} altKey="shots.phoneCall.alt" />
        <Shot shot={SHOTS.phoneInvite} altKey="shots.phoneInvite.alt" />
      </div>

      <ul className="platforms">
        {PLATFORMS.map((platform) => (
          <li key={platform}>
            {/*
              Opens in a new tab by owner decision, and the rel is not optional company for
              it: without `noopener` the page we open gets a live `window.opener` handle to
              this one and can navigate it somewhere else — textbook tabnabbing, and a hole
              that would be especially poor on a page whose argument is that it is careful.
              `noreferrer` additionally keeps this page's address out of the store's logs; the
              host already sends Referrer-Policy: no-referrer, and this pins the same
              guarantee to the markup rather than to a server config that could be changed.

              ⚠ BOTH ATTRIBUTES BELONG ON THIS ELEMENT, NOT ON THE ACTION LABEL INSIDE IT. The
              whole cell is the link, so putting them on the inner span would make the tab
              behaviour depend on where in the card the cursor happened to land.

              The new tab IS announced, by the visually hidden span at the end of the card.
              ⚠ Translation of `platforms.opensInNewTab` is still landing across the other 22
              locales; until it does they render the key name instead of a sentence. That is
              deliberate and harmless here — the element is .sr-only, so it is out of flow and
              cannot disturb the layout, and a key name read aloud is still better than
              silence about a link that hijacks the tab.
            */}
            <a
              className="platform"
              href={href[platform]}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h3 className="platform__name">{t(`platforms.${platform}.name`)}</h3>

              {has(`platforms.${platform}.detail`) ? (
                <p className="platform__detail">{t(`platforms.${platform}.detail`)}</p>
              ) : null}

              {/* The action line, and on the two store platforms the store mark beside it. It
                  sits at the BOTTOM of the card (the wrapper carries the auto margin), so a
                  card with one line of detail still lines its action up with its neighbours —
                  which is also why the mark went here rather than above the title, where two
                  cards out of three would have pushed their headings out of line. */}
              {has(`platforms.${platform}.action`) ? (
                <span className="platform__go">
                  {platform === "web" ? null : <StoreMark platform={platform} />}
                  <span className="platform__action">{t(`platforms.${platform}.action`)}</span>
                </span>
              ) : null}

              {/* Announces the new tab to a screen reader without repeating on screen what
                  the browser's own tab strip already shows. */}
              <span className="sr-only">{t("platforms.opensInNewTab")}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
